import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, ChevronDown, Sun, Moon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import saigeLogo from '../assets/saige-logo.png';

/* ── Fade-in observer hook ─────────────────────────────── */
const useFadeIn = () => {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { el.classList.add('fade-in-visible'); observer.unobserve(el); } },
            { threshold: 0.15 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    return ref;
};

const FadeIn = ({ children, className = '', delay = 0 }) => {
    const ref = useFadeIn();
    return (
        <div
            ref={ref}
            className={`fade-in-hidden ${className}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            {children}
        </div>
    );
};

/* ── FAQ Item ──────────────────────────────────────────── */
const FAQItem = ({ question, answer, isDark }) => {
    const [open, setOpen] = useState(false);
    return (
        <div style={{ borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'}` }}>
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between py-5 text-left group"
            >
                <span
                    className="text-base font-medium pr-4 transition-colors"
                    style={{ color: isDark ? '#ededed' : '#1d1d1f' }}
                >
                    {question}
                </span>
                <ChevronDown
                    size={18}
                    className={`shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
                    style={{ color: isDark ? '#666' : '#999' }}
                />
            </button>
            <div
                className={`overflow-hidden transition-all duration-300 ease-out ${open ? 'max-h-40 pb-5' : 'max-h-0'}`}
            >
                <p className="text-sm leading-relaxed" style={{ color: isDark ? '#999' : '#666' }}>{answer}</p>
            </div>
        </div>
    );
};

/* ── Logo Component ────────────────────────────────────── */
const SaigeLogo = ({ height = 28 }) => (
    <img
        src={saigeLogo}
        alt="Saige"
        style={{ height, width: 'auto', objectFit: 'contain', display: 'block' }}
    />
);

/* ── Color tokens ──────────────────────────────────────── */
const t = (isDark) => ({
    pageBg:       isDark ? '#171717' : '#ffffff',
    sectionBg:    isDark ? '#1c1c1c' : '#fafafa',
    cardBg:       isDark ? '#242424' : '#ffffff',
    cardBorder:   isDark ? '#333'    : '#eee',
    navBg:        isDark ? 'rgba(23,23,23,0.92)' : 'rgba(255,255,255,0.8)',
    navBorder:    isDark ? '#2a2a2a' : '#f5f5f7',
    textPrimary:  isDark ? '#ededed' : '#1d1d1f',
    textSecondary:isDark ? '#999'    : '#666',
    textMuted:    isDark ? '#666'    : '#999',
    footerText:   isDark ? '#555'    : '#ccc',
    footerLink:   isDark ? '#666'    : '#bbb',
    footerHover:  isDark ? '#999'    : '#888',
    selectionBg:  isDark ? 'rgba(255,193,7,0.2)' : 'undefined',
});

/* ── Landing Page ──────────────────────────────────────── */
const LandingPage = ({ onGetStarted, onLogin, onAboutUs, onProduct }) => {
    const { isDark, toggleTheme } = useTheme();
    const c = t(isDark);

    return (
        <div
            className="min-h-screen w-full font-sans overflow-x-hidden"
            style={{
                backgroundColor: c.pageBg,
                color: c.textPrimary,
                ...(isDark ? {} : { selectionBackground: 'rgb(255, 243, 205)' }),
            }}
        >

            {/* ── Inline animation styles ── */}
            <style>{`
                .fade-in-hidden {
                    opacity: 0;
                    transform: translateY(16px);
                    transition: opacity 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94),
                                transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                }
                .fade-in-visible {
                    opacity: 1;
                    transform: translateY(0);
                }
                ::selection {
                    background: ${isDark ? 'rgba(255,193,7,0.25)' : 'rgb(255,243,205)'};
                }
            `}</style>

            {/* ── Nav ── */}
            <nav
                className="fixed top-0 inset-x-0 z-50 backdrop-blur-md"
                style={{
                    backgroundColor: c.navBg,
                    borderBottom: `1px solid ${c.navBorder}`,
                }}
            >
                <div className="max-w-[960px] mx-auto px-6 h-14 flex justify-between items-center">
                    <div className="flex items-center gap-2">
                        <SaigeLogo height={22} />
                        <span
                            className="font-semibold tracking-tight text-[15px]"
                            style={{ color: c.textPrimary }}
                        >Saige</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                        {/* About Us & Product nav links */}
                        <button
                            onClick={onAboutUs}
                            id="nav-about-us"
                            className="transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC107] rounded"
                            style={{ color: c.textSecondary, cursor: 'pointer', background: 'none', border: 'none', fontSize: 'inherit', fontFamily: 'inherit' }}
                            onMouseEnter={e => e.target.style.color = '#FFC107'}
                            onMouseLeave={e => e.target.style.color = c.textSecondary}
                        >
                            About Us
                        </button>
                        <button
                            onClick={onProduct}
                            id="nav-product"
                            className="transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC107] rounded"
                            style={{ color: c.textSecondary, cursor: 'pointer', background: 'none', border: 'none', fontSize: 'inherit', fontFamily: 'inherit' }}
                            onMouseEnter={e => e.target.style.color = '#FFC107'}
                            onMouseLeave={e => e.target.style.color = c.textSecondary}
                        >
                            Product
                        </button>
                        {/* Theme toggle */}
                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-lg transition-colors"
                            style={{
                                color: c.textSecondary,
                                backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                            }}
                            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        >
                            {isDark ? <Sun size={16} /> : <Moon size={16} />}
                        </button>
                        <button
                            onClick={onLogin}
                            className="transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC107] rounded"
                            style={{ color: c.textSecondary }}
                            onMouseEnter={e => e.target.style.color = c.textPrimary}
                            onMouseLeave={e => e.target.style.color = c.textSecondary}
                        >
                            Log in
                        </button>
                        <button
                            onClick={onGetStarted}
                            className="bg-[#FFC107] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#e6ad00] transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC107] focus-visible:ring-offset-2"
                        >
                            Get started
                        </button>
                    </div>
                </div>
            </nav>

            {/* ══════════════════════════════════════════════════ */}
            {/* ── Section 1 · Hero ──────────────────────────── */}
            {/* ══════════════════════════════════════════════════ */}
            <header className="min-h-[90vh] flex items-center justify-center px-6">
                <div className="max-w-[680px] w-full text-center pt-14">
                    <FadeIn>
                        <h1
                            className="font-bold leading-[1.06] mb-6"
                            style={{
                                fontSize: 'clamp(2.5rem, 5vw, 4.5rem)',
                                letterSpacing: '-0.03em',
                                color: c.textPrimary,
                            }}
                        >
                            Save 5+ Hours Every Week<br />
                            on Lesson Planning
                        </h1>
                    </FadeIn>

                    <FadeIn delay={150}>
                        <p
                            className="font-normal leading-relaxed max-w-xl mx-auto mb-10"
                            style={{
                                fontSize: 'clamp(1.125rem, 2vw, 1.375rem)',
                                color: c.textSecondary,
                            }}
                        >
                            Standards-aligned, differentiated lesson plans — generated in
                            under 60 seconds and ready to teach tomorrow.
                        </p>
                    </FadeIn>

                    <FadeIn delay={300}>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-8">
                            <button
                                onClick={onGetStarted}
                                className="group bg-[#FFC107] text-white px-8 py-3.5 rounded-xl text-base font-semibold
                                           hover:bg-[#e6ad00] hover:-translate-y-0.5 hover:shadow-lg
                                           transition-all duration-200
                                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC107] focus-visible:ring-offset-2
                                           flex items-center gap-2"
                            >
                                Create a Free Lesson Now
                                <ArrowRight size={18} className="transition-transform group-hover:translate-x-0.5" />
                            </button>
                            <button
                                onClick={onLogin}
                                className="px-8 py-3.5 rounded-xl text-base font-medium
                                           border-2 border-[#FFC107]
                                           hover:bg-[#FFC107]/10 hover:-translate-y-0.5
                                           transition-all duration-200
                                           focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC107] focus-visible:ring-offset-2"
                                style={{ color: c.textPrimary }}
                            >
                                Log in
                            </button>
                        </div>
                        <p className="text-sm text-center" style={{ color: c.textMuted }}>Free forever · No credit card required</p>
                    </FadeIn>

                    {/* Stat cards */}
                    <FadeIn delay={400}>
                        <div className="flex items-center justify-center gap-3 md:gap-4 mb-16 flex-wrap">
                            {[
                                { value: '5+ hrs', label: 'Saved weekly' },
                                { value: '60 sec', label: 'Per lesson' },
                                { value: '100%', label: 'Your control' },
                            ].map(stat => (
                                <div
                                    key={stat.label}
                                    className="rounded-xl px-5 py-3 text-center"
                                    style={{
                                        backgroundColor: c.cardBg,
                                        border: `1px solid ${c.cardBorder}`,
                                    }}
                                >
                                    <p className="text-lg font-bold" style={{ color: c.textPrimary }}>{stat.value}</p>
                                    <p className="text-[10px] uppercase tracking-wider" style={{ color: c.textMuted }}>{stat.label}</p>
                                </div>
                            ))}
                        </div>
                    </FadeIn>
                </div>
            </header>

            {/* ══════════════════════════════════════════════════ */}
            {/* ── Section 2 · Benefit Summary ─────────────── */}
            {/* ══════════════════════════════════════════════════ */}
            <section className="py-24 md:py-32 px-6" style={{ backgroundColor: c.sectionBg }}>
                <div className="max-w-[680px] mx-auto text-center">
                    <FadeIn>
                        <h2
                            className="font-bold mb-10"
                            style={{
                                fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
                                letterSpacing: '-0.02em',
                                color: c.textPrimary,
                            }}
                        >
                            Deliver your best lesson once.<br />
                            Let Saige handle the rest.
                        </h2>
                    </FadeIn>
                    <FadeIn delay={120}>
                        <ul className="text-left max-w-md mx-auto space-y-4 text-base leading-relaxed" style={{ color: isDark ? '#bbb' : '#444' }}>
                            <li className="flex items-start gap-3">
                                <span className="inline-block mt-1.5 w-1.5 h-1.5 rounded-full bg-[#FFC107] shrink-0"></span>
                                <span>Save <strong>5+ hours per week</strong> on planning and prep</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="inline-block mt-1.5 w-1.5 h-1.5 rounded-full bg-[#FFC107] shrink-0"></span>
                                <span>TEKS-aligned, structured lessons ready to teach</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="inline-block mt-1.5 w-1.5 h-1.5 rounded-full bg-[#FFC107] shrink-0"></span>
                                <span>Replayable instructional delivery videos</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="inline-block mt-1.5 w-1.5 h-1.5 rounded-full bg-[#FFC107] shrink-0"></span>
                                <span>Built for district-wide consistency</span>
                            </li>
                        </ul>
                    </FadeIn>
                </div>
            </section>

            {/* ══════════════════════════════════════════════════ */}
            {/* ── Section 3 · How It Works ────────────────── */}
            {/* ══════════════════════════════════════════════════ */}
            <section className="py-28 md:py-36 px-6" style={{ backgroundColor: c.pageBg }}>
                <div className="max-w-[680px] mx-auto text-center">
                    <FadeIn>
                        <h2
                            className="font-bold mb-20"
                            style={{
                                fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
                                letterSpacing: '-0.02em',
                                color: c.textPrimary,
                            }}
                        >
                            Three steps. One complete lesson.
                        </h2>
                    </FadeIn>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-10">
                        {[
                            { num: '01', title: 'Enter Your Topic', desc: 'Type a standard, unit, or lesson title — Saige handles the rest.' },
                            { num: '02', title: 'Review and Customize', desc: 'Edit objectives, activities, and timing — keep full control over every detail.' },
                            { num: '03', title: 'Teach Tomorrow', desc: 'Export to PDF, PowerPoint, or share directly with students.' },
                        ].map((step, i) => (
                            <FadeIn key={step.num} delay={i * 120}>
                                <div className="flex flex-col items-center">
                                    <span className="text-sm font-medium text-[#FFC107] mb-4">{step.num}</span>
                                    <h3
                                        className="text-lg font-semibold mb-2"
                                        style={{ color: c.textPrimary }}
                                    >{step.title}</h3>
                                    <p
                                        className="text-sm leading-relaxed max-w-[220px]"
                                        style={{ color: c.textSecondary }}
                                    >{step.desc}</p>
                                </div>
                            </FadeIn>
                        ))}
                    </div>
                </div>
            </section>

            {/* ══════════════════════════════════════════════════ */}
            {/* ── Section A · Accent (Mid-page reinforcement) ─ */}
            {/* ══════════════════════════════════════════════════ */}
            <section className="py-24 md:py-32 px-6" style={{ background: '#FFC107' }}>
                <div className="max-w-[680px] mx-auto text-center">
                    <FadeIn>
                        <h2
                            className="font-bold text-white mb-4"
                            style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)', letterSpacing: '-0.02em' }}
                        >
                            Consistency Across Every Period.
                        </h2>
                        <p className="text-white/80 text-base md:text-lg">
                            Same standards. Same rigor. Every class, every day — without the Sunday night grind.
                        </p>
                    </FadeIn>
                </div>
            </section>

            {/* ══════════════════════════════════════════════════ */}
            {/* ── Section 4 · Common Questions ────────────── */}
            {/* ══════════════════════════════════════════════════ */}
            <section className="py-28 md:py-36 px-6" style={{ backgroundColor: c.pageBg }}>
                <div className="max-w-[640px] mx-auto">
                    <FadeIn>
                        <h2
                            className="font-bold text-center mb-12"
                            style={{
                                fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
                                letterSpacing: '-0.02em',
                                color: c.textPrimary,
                            }}
                        >
                            Common questions
                        </h2>
                    </FadeIn>
                    <FadeIn delay={100}>
                        <div>
                            <FAQItem
                                isDark={isDark}
                                question="Will it actually match my standards?"
                                answer="Saige maps every lesson to your state standards and TEKS. You review and approve before teaching — nothing goes to your students without your sign-off."
                            />
                            <FAQItem
                                isDark={isDark}
                                question="I don't want cookie-cutter lessons."
                                answer="Every lesson is fully editable. Add your voice, adjust timing, swap activities — it's your plan. Saige gives you a strong starting point, not a finished product."
                            />
                            <FAQItem
                                isDark={isDark}
                                question="Is my student data safe?"
                                answer="Saige is built FERPA-aligned from the ground up. Student names are never sent to AI models or stored in logs. We use code names for all student-facing features."
                            />
                            <FAQItem
                                isDark={isDark}
                                question="What does it cost?"
                                answer="Saige is free to start — no credit card, no trial expiration. Generate lessons immediately and decide if it works for your classroom."
                            />
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* ══════════════════════════════════════════════════ */}
            {/* ── Section 5 · Trust ───────────────────────── */}
            {/* ══════════════════════════════════════════════════ */}
            <section className="py-28 md:py-36 px-6" style={{ backgroundColor: c.sectionBg }}>
                <div className="max-w-[680px] mx-auto text-center">
                    <FadeIn>
                        <div className="space-y-5">
                            <p className="font-medium" style={{ fontSize: 'clamp(1rem, 1.8vw, 1.25rem)', color: c.textPrimary }}>
                                Built by a classroom teacher who spent 6+ years in the same planning grind.
                            </p>
                            <p style={{ fontSize: 'clamp(1rem, 1.8vw, 1.25rem)', color: c.textSecondary }}>
                                Your lessons. Your edits. Your voice — always.
                            </p>
                            <p style={{ fontSize: 'clamp(1rem, 1.8vw, 1.25rem)', color: c.textSecondary }}>
                                Sign up in 30 seconds. No training, no onboarding calls, no credit card.
                            </p>
                        </div>
                    </FadeIn>
                </div>
            </section>

            {/* ══════════════════════════════════════════════════ */}
            {/* ── Section B · Final CTA (Accent) ──────────── */}
            {/* ══════════════════════════════════════════════════ */}
            <section className="py-28 md:py-36 px-6" style={{ background: '#FFC107' }}>
                <div className="max-w-[680px] mx-auto text-center">
                    <FadeIn>
                        <h2
                            className="font-bold text-white mb-4"
                            style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)', letterSpacing: '-0.02em' }}
                        >
                            Ready to Reclaim 5 Hours a Week?
                        </h2>
                    </FadeIn>
                    <FadeIn delay={100}>
                        <p className="text-white/80 text-base mb-10">
                            Join teachers saving 5+ hours every week with Saige.
                        </p>
                    </FadeIn>
                    <FadeIn delay={200}>
                        <button
                            onClick={onGetStarted}
                            className="group bg-white text-[#1d1d1f] px-8 py-3.5 rounded-xl text-base font-semibold
                                       hover:bg-[#f5f5f5] hover:-translate-y-0.5 hover:shadow-lg
                                       transition-all duration-200
                                       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#FFC107]
                                       inline-flex items-center gap-2"
                        >
                            Start Planning for Free
                            <ArrowRight size={18} className="transition-transform group-hover:translate-x-0.5" />
                        </button>
                    </FadeIn>
                </div>
            </section>

            {/* ── Footer ── */}
            <footer className="py-12 text-center text-xs tracking-wide space-y-2" style={{ backgroundColor: c.pageBg }}>
                <p style={{ color: c.footerText }}>&copy; 2026 Saige</p>
                <a
                    href="/privacy"
                    className="transition-colors underline"
                    style={{ color: c.footerLink }}
                    onMouseEnter={e => e.target.style.color = c.footerHover}
                    onMouseLeave={e => e.target.style.color = c.footerLink}
                >
                    Privacy Policy
                </a>
            </footer>

        </div>
    );
};

export default LandingPage;
